import tern from 'tern';
import {searchVisitor, typeFinder} from 'tern/lib/infer';

import {getOwnAttr} from 'common/util';

class FoundExpression {
  constructor(node, state, ancestors) {
    this.node = node;
    this.state = state;
    this.ancestors = ancestors;
  }

  checkAncestry(exprChecklist) {
    for (let depth = 0; depth < exprChecklist.length; depth++) {
      const check = exprChecklist[depth];
      const expr = this.ancestors[depth];
      if (!(expr && check(expr))) {
        return false;
      }
    }
    return true;
  }
}

// Wrapper for `tern.js` which is a standalone code-analysis engine
export class TernServer {
  get SINGLE_FILE_NAME() {
    return this.filename;
  }

  constructor({editor, filename, includeKeywords = true, caseInsensitive = false}) {
    this.editor = editor;
    this.filename = filename;
    this.baseServer = new tern.Server({
      getFile: (name) => (name === this.SINGLE_FILE_NAME && editor ? this.getNormalizedEditorValue() : null),
      defs: [],
    });
    this.baseServer.addFile(this.SINGLE_FILE_NAME);
    this.includeKeywords = includeKeywords;
    this.caseInsensitive = caseInsensitive;
  }

  // Normalize new-line characters
  getNormalizedEditorValue() {
    return this.editor.getValue().replace(new RegExp(this.editor.session.doc.getNewLineCharacter(), `g`), `\n`);
  }

  registerDefs(defs, atFront) {
    this.baseServer.deleteDefs(defs[`!name`]);
    this.baseServer.addDefs(defs, atFront);
  }

  getDefs(defsName) {
    return this.baseServer.defs.find((defs) => defs[`!name`] === defsName);
  }

  getTernDefFromAPIDefinition(apiDefinition) {
    const def = {};

    if (apiDefinition.type === `function`) {
      const argString = Array(apiDefinition.numArgs)
        .fill(`arg`)
        .map((prefix, idx) => `${prefix}${idx}: ?`)
        .join(`, `);
      def[`!type`] = `fn(${argString})`;
    } else {
      def[`!type`] = apiDefinition.type;
    }

    if (apiDefinition.typeAnnotation) {
      def[`!doc`] = apiDefinition.typeAnnotation;
    }

    return def;
  }

  generateDefsFromAPIDefinitions(defsName, apiDefinitions, includePrivate = false) {
    const defs = {
      '!name': defsName,
    };
    Object.entries(apiDefinitions)
      .filter(([, apiDefinition]) => !apiDefinition.private || includePrivate)
      .forEach(([path, apiDefinition]) => {
        const properties = path.split(`.`);
        const builtinName = properties.pop();

        let currentProp = defs;
        for (const name of properties) {
          if (!currentProp[name]) {
            currentProp[name] = {};
          }
          currentProp = getOwnAttr(currentProp, name);
        }

        currentProp[builtinName] = this.getTernDefFromAPIDefinition(apiDefinition);
      });
    return defs;
  }

  resolvePositionToIndex(lineIdx, charIdx) {
    const file = this.baseServer.findFile(this.SINGLE_FILE_NAME);
    return tern.resolvePos(file, {line: lineIdx, ch: charIdx});
  }

  // Code adapted from "acornjs" and "ternjs" projects, both of which are MIT licensed
  // https://github.com/acornjs/acorn/blob/master/LICENSE
  // https://github.com/ternjs/tern/blob/master/LICENSE
  // eslint-disable-next-line sonarjs/cognitive-complexity
  findSurroundingExpression(cursorIdx) {
    const file = this.baseServer.findFile(this.SINGLE_FILE_NAME);
    const ancestors = [];
    try {
      const test = function (node) {
        if (node.start > cursorIdx) return false;
        if (node.type === `Identifier` && node.name === `✖`) return false;
        return Object.prototype.hasOwnProperty.call(typeFinder, node.type);
      };
      (function c(node, st, override) {
        if (node.start > cursorIdx || node.end < cursorIdx) {
          return;
        }

        const visit = searchVisitor[override || node.type];
        const isNew = node !== ancestors[ancestors.length - 1];

        if (isNew) {
          ancestors.push(node);
        }
        visit(node, st, c);
        if (test(node)) {
          throw new FoundExpression(node, st, ancestors.reverse());
        } // reverse ancestors because narrow -> broad ascending scope order is more useful
        if (isNew) {
          ancestors.pop(node);
        }
      })(file.ast, file.scope);
    } catch (err) {
      if (err instanceof FoundExpression) {
        return err;
      }
      throw err;
    }
  }

  requestCompletions(lineIdx, charIdx, inputText) {
    if (!inputText && this.editor) {
      inputText = this.getNormalizedEditorValue();
    }
    return new Promise((resolve, reject) => {
      this.baseServer.request(
        {
          files: [
            {
              type: `full`,
              name: this.SINGLE_FILE_NAME,
              text: inputText,
            },
          ],
          query: {
            type: `completions`,
            file: this.SINGLE_FILE_NAME,
            end: {line: lineIdx, ch: charIdx},
            filter: true,
            types: true,
            docs: true,
            urls: true,
            origins: true,
            guess: false,
            lineCharPositions: true,
            includeKeywords: this.includeKeywords,
            caseInsensitive: this.caseInsensitive,
          },
        },
        (error, data) => {
          if (error) {
            reject(error);
          }
          resolve(data);
        },
      );
    });
  }
}
